/**
 * Auth Store
 * SpecReferenceID: FRONTEND-WEB-V1
 * Requirements: FRONTEND-WEB-V1-R01
 *
 * Zustand store for authentication state management with persistence.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { UserSession } from '@/types';
import { authApi, getErrorMessage } from '@/lib/api';

interface AuthState {
  user: UserSession | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  clearError: () => void;
  loadStoredAuth: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      login: async (username: string, password: string) => {
        set({ isLoading: true, error: null });
        try {
          const response = await authApi.login(username, password);
          set({
            user: response.user,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });
        } catch (err) {
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: getErrorMessage(err),
          });
          throw err;
        }
      },

      logout: () => {
        set({
          user: null,
          isAuthenticated: false,
          error: null,
        });
        // Clear token and conversation history from storage
        localStorage.removeItem('auth-token');
        localStorage.removeItem('galen-conversations-storage');
      },

      clearError: () => {
        set({ error: null });
      },

      loadStoredAuth: () => {
        const state = get();
        // Check if stored session is still valid
        if (state.user && state.user.expiresAt) {
          const isExpired = new Date(state.user.expiresAt) < new Date();
          if (isExpired) {
            set({
              user: null,
              isAuthenticated: false,
            });
          }
        }
      },
    }),
    {
      name: 'galen-auth-storage',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

// Helper hooks for cleaner component usage
export const useUser = (): UserSession | null => useAuthStore((state) => state.user);
export const useIsAuthenticated = (): boolean => useAuthStore((state) => state.isAuthenticated);
export const useIsLoading = (): boolean => useAuthStore((state) => state.isLoading);
